import React from 'react';
import { Link } from 'react-router-dom';
import useScrollReveal from '../hooks/useScrollReveal';
import useSeo from '../hooks/useSeo';
import { COMPANY } from '../mock';

const UPDATED = 'June 2026';

const CONTENT = {
  privacy: {
    title: 'Privacy Policy',
    intro: 'This Privacy Policy explains how LRG Holdings collects, uses and protects personal information provided through this website.',
    sections: [
      ['Who we are', `LRG Holdings is a Botswana based investment and development company headquartered in ${COMPANY.location}. For privacy enquiries contact ${COMPANY.email}.`],
      ['Information we collect', 'When you use the contact form we collect your name, email address and message. When you subscribe to updates we collect your email address. We also record the time of submission and basic technical information such as browser type for security and spam prevention.'],
      ['How we use your information', 'We use contact form submissions to respond to your enquiry. We use newsletter subscriptions to send occasional updates about LRG Holdings and its sectors. We do not sell or rent personal information to third parties.'],
      ['Legal basis and consent', 'We process contact enquiries on the basis of your request and our legitimate interest in responding. Newsletter subscriptions are processed on the basis of your consent, which you may withdraw at any time by emailing us.'],
      ['Storage and security', 'Submissions are stored in a secured database accessible only to authorised LRG Holdings personnel. We retain contact enquiries for as long as needed to respond and maintain a record of correspondence, and newsletter subscriptions until you unsubscribe.'],
      ['Your rights', 'You may request access to, correction of, or deletion of your personal information, and you may object to or restrict its processing. To exercise any of these rights, email us at the address above.'],
      ['Third party links', 'This website links to external sites including Botswana Brilliance, LRG Squash, LRG Leather Botswana, partner and press websites. We are not responsible for the privacy practices of those sites.'],
      ['Changes', 'We may update this policy from time to time. The date of the latest revision is shown at the top of this page.'],
    ],
  },
  terms: {
    title: 'Terms of Use',
    intro: 'By accessing this website you agree to the following terms. If you do not agree, please do not use the site.',
    sections: [
      ['Use of the website', 'This website is provided for general information about LRG Holdings and its activities. You may view, download and print content for personal, non commercial use provided that copyright and other proprietary notices are retained.'],
      ['Intellectual property', 'All text, photographs, logos and design elements on this website are the property of LRG Holdings or are used with permission. Third party trademarks, including those of GemEx, iTraceiT, Sarine, the University of Botswana and the University of Chicago, belong to their respective owners.'],
      ['No offer or advice', 'Nothing on this website constitutes an offer to sell or a solicitation to buy any security, diamond or other asset, nor does it constitute investment, legal or professional advice. Information about projects in development, including LRG Leather Botswana, reflects their status at the date of publication and may change.'],
      ['Accuracy', 'We take care to keep information accurate and current, including dates, positions, statistics and institutional names, but we make no warranty that the content is complete or error free.'],
      ['External links', 'Links to third party websites are provided for convenience. LRG Holdings does not endorse and is not responsible for the content of external sites.'],
      ['Limitation of liability', 'To the fullest extent permitted by law, LRG Holdings shall not be liable for any loss or damage arising from the use of, or inability to use, this website.'],
      ['Governing law', 'These terms are governed by the laws of the Republic of Botswana.'],
    ],
  },
  cookies: {
    title: 'Cookie Policy',
    intro: 'This Cookie Policy explains how this website uses cookies and similar technologies.',
    sections: [
      ['What are cookies', 'Cookies are small text files placed on your device when you visit a website. Similar technologies include browser local storage.'],
      ['How we use them', 'This website does not set cookies for advertising, profiling or cross site tracking, and it does not display a cookie consent banner because none is required for its current operation. Only technically necessary items may be stored by your browser.'],
      ['Analytics', 'If we introduce analytics in future, we will update this policy and, where required, ask for your consent before setting analytics cookies.'],
      ['Managing cookies', 'You can clear cookies and local storage at any time through your browser settings.'],
      ['Contact', `Questions about this policy may be sent to ${COMPANY.email}.`],
    ],
  },
};

const Legal = ({ page }) => {
  useScrollReveal([page]);
  const c = CONTENT[page];
  useSeo({ title: c.title, description: c.intro, path: { privacy: '/privacy-policy', terms: '/terms-of-use', cookies: '/cookie-policy' }[page] });

  return (
    <div className="bg-[#f7f4ee]" data-testid={`legal-${page}`}>
      <section className="bg-[#071627] pt-36 pb-16">
        <div className="container-lg">
          <p className="eyebrow eyebrow-line eyebrow-light mb-6">Legal / Last updated {UPDATED}</p>
          <h1 className="font-serif text-[#f7f4ee] text-4xl md:text-6xl leading-[1.05]">{c.title}</h1>
        </div>
      </section>
      <section className="py-20">
        <div className="container-lg grid lg:grid-cols-12 gap-14">
          <aside className="lg:col-span-3">
            <p className="eyebrow mb-4">Policies</p>
            <ul className="space-y-3 text-sm">
              {[['privacy', 'Privacy Policy', '/privacy-policy'], ['terms', 'Terms of Use', '/terms-of-use'], ['cookies', 'Cookie Policy', '/cookie-policy']].map(([k, l, to]) => (
                <li key={k}><Link to={to} className={`hover:text-[#a3813f] transition-colors ${k === page ? 'text-[#a3813f]' : 'text-[#0a1f38]'}`}>{l}</Link></li>
              ))}
            </ul>
          </aside>
          <article className="lg:col-span-8 space-y-10">
            <p className="text-[#3a3f47] font-light text-lg leading-relaxed">{c.intro}</p>
            {c.sections.map(([h, t], i) => (
              <div key={h} className="border-t border-[#0a1f38]/10 pt-8">
                <p className="numeral text-xl mb-2">0{i + 1}</p>
                <h2 className="font-serif text-2xl md:text-3xl text-[#0a1f38] mb-3">{h}</h2>
                <p className="text-[#3a3f47] font-light leading-relaxed">{t}</p>
              </div>
            ))}
          </article>
        </div>
      </section>
    </div>
  );
};

export default Legal;
